import { Injectable, OnDestroy } from '@angular/core';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { MeterView } from '@app/shared/models/meter-data/meter-view';
import { EnergyTypesName } from '@app/shared/enums';
import { EfficiencyAnalysisDto } from '@app/shared/models/efficiency-analysis/efficiency-analysis-dto';
import { EfficiencyAnalysisMeters } from '@app/shared/models/efficiency-analysis/efficiency-analysis-meters';
import { StringUtility } from '@app/shared/string-utility';
import { isEmpty, isNil } from 'lodash';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { BuildingEfficiencyWizardStore } from '../building-efficiency-wizard.component.store';

@Injectable({
  providedIn: 'root',
})
export class ElectricityMeterSelectionService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public metersStore: BehaviorSubject<EfficiencyAnalysisMeters[]> = new BehaviorSubject<EfficiencyAnalysisMeters[]>([]);
  public isConsumptionAutoSuggestedStore: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public hasSuggestedConsumptionsStore: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public efficiencyMetersStore: BehaviorSubject<EfficiencyAnalysisMeters[]> = new BehaviorSubject<EfficiencyAnalysisMeters[]>([]);
  public totalConsumptionStore: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public wizardDataStore: BehaviorSubject<IRawWizardData | null> = new BehaviorSubject<IRawWizardData | null>(null);
  private efficiencyAnalysisStore: BehaviorSubject<EfficiencyAnalysisDto> = new BehaviorSubject<EfficiencyAnalysisDto>({} as EfficiencyAnalysisDto);

  public wizardData: Observable<IRawWizardData | null> = this.wizardDataStore.asObservable();
  public meters: Observable<EfficiencyAnalysisMeters[]> = this.metersStore.asObservable();
  public isConsumptionAutoSuggested: Observable<boolean> = this.isConsumptionAutoSuggestedStore.asObservable();
  public hasSuggestedConsumptions: Observable<boolean> = this.hasSuggestedConsumptionsStore.asObservable();
  public startDate = '';
  public endDate = '';
  public efficiencyMeters: Observable<EfficiencyAnalysisMeters[]> = this.efficiencyMetersStore
    .asObservable()
    .pipe(
      map((meters: EfficiencyAnalysisMeters[]): EfficiencyAnalysisMeters[] =>
        meters.filter((meter: EfficiencyAnalysisMeters) => StringUtility.areStringsIdentical(meter.energyTypeName, 'electricity'))
      )
    );

  public totalConsumption = this.totalConsumptionStore.asObservable();
  keyPressed = false;
  isElectricityToggleOn = false;

  constructor(private buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore) {
    this.buildingEfficiencyWizardStore.wizardData$
      .pipe(
        filter((wizard: IRawWizardData | null): wizard is IRawWizardData => !isNil(wizard)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data: IRawWizardData) => {
        this.getDate(data);
        this.wizardDataStore.next(data);
        this.isElectricityToggleOn = data.isElectricityValueSuggested ?? false;
      });

    this.buildingEfficiencyWizardStore.efficiencyAnalysis$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((analysis: EfficiencyAnalysisDto) => this.efficiencyAnalysisStore.next(analysis));

    this.buildingEfficiencyWizardStore.efficiencyAnalysisMeters$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((meters: EfficiencyAnalysisMeters[]) => this.efficiencyMetersStore.next(meters));

    this.wizardDataStore
      .asObservable()
      .pipe(
        filter((wizard: IRawWizardData | null): wizard is IRawWizardData => !isNil(wizard)),
        withLatestFrom(this.meters),
        map(([wizard, meters]: [IRawWizardData, EfficiencyAnalysisMeters[]]): IRawWizardData => {
          wizard.meterList.forEach((meter: MeterView) => {
            const consumption = meters.find((c: EfficiencyAnalysisMeters) => c.meter.guid === meter.guid);
            if (!isNil(consumption)) {
              const determineConsumption = () => {
                if (!isNil(consumption.value)) {
                  if (typeof consumption.value === 'string') {
                    return this.unlocalize(consumption.value);
                  }
                  return consumption.value;
                }
              };

              meter.consumption = determineConsumption();
              meter.isChecked = consumption.isSelected;
            }
          });
          return wizard;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    combineLatest([this.efficiencyMeters, this.meters])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([consumptions, meters]: [EfficiencyAnalysisMeters[], EfficiencyAnalysisMeters[]]) => {
        const metersWithSuggestedConsumption = meters.filter((meter: EfficiencyAnalysisMeters) => typeof meter.value === 'number' && meter.value >= 0);
        if (metersWithSuggestedConsumption.length > 0) {
          this.hasSuggestedConsumptionsStore.next(true);
        }
      });

    combineLatest([this.efficiencyMeters, this.buildingEfficiencyWizardStore.meters$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([consumptions, meters]: [EfficiencyAnalysisMeters[], MeterView[]]) => {
        this.setMeters(
          this.mapMetersToSavedConsumptions(
            !isNil(meters) && !isEmpty(meters)
              ? meters.filter((meter: MeterView) => !StringUtility.areStringsIdentical(meter.energyTypesName, EnergyTypesName.gas))
              : [],
            consumptions
          )
        );
        if (this.isElectricityToggleOn) {
          this.isConsumptionAutoSuggestedStore.next(true);
        } else {
          consumptions.length > 0 ? this.isConsumptionAutoSuggestedStore.next(false) : this.isConsumptionAutoSuggestedStore.next(true);
        }
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private mapMetersToSavedConsumptions(meters: MeterView[], consumptions: EfficiencyAnalysisMeters[]): EfficiencyAnalysisMeters[] {
    if (meters.length === 0) {
      //TODO: verify change with b2m team; MeterView.meterNumber and measurementLocationKey is not nullable but assigned as null on code
      meters.push({ meterNumber: '', measurementLocationKey: '' } as MeterView);
    }
    return meters.map(
      (meter: MeterView) =>
        ({
          meter: meter,
          ...(consumptions.length > 0
            ? consumptions.find((cons: EfficiencyAnalysisMeters) => cons.meterNumber === meter.meterNumber)
            : { energyTypeName: 'electricity' }),
        } as EfficiencyAnalysisMeters)
    );
  }

  getMeters = () =>
    this.buildingEfficiencyWizardStore.wizardData$.pipe(
      filter((wizard: IRawWizardData | null): wizard is IRawWizardData => !isNil(wizard) && !isEmpty(wizard)),
      map((wizard: IRawWizardData) => wizard.meterList)
    );

  getDate = (data: IRawWizardData) => {
    if (data.hasTimePeriod) {
      this.startDate = data.userDefinedStartDate;
      this.endDate = data.userDefinedEndDate;
    } else {
      this.startDate = data.defaultStartDate;
      this.endDate = data.defaultEndDate;
    }
  };

  setIsConsumptionAutoSuggested = (value: boolean) => this.isConsumptionAutoSuggestedStore.next(value);

  setMeters = (meters: EfficiencyAnalysisMeters[]) => this.metersStore.next(this.filterGasMeters(meters));

  filterGasMeters = (meters: EfficiencyAnalysisMeters[]) =>
    !isNil(meters) && !isEmpty(meters)
      ? meters.filter((ea: EfficiencyAnalysisMeters) => !StringUtility.areStringsIdentical(ea.meter.energyTypesName, EnergyTypesName.gas))
      : [];

  public mapMetersToEfficiencyMeters(efficiencyAnalysisId: string, meters = this.metersStore.getValue()): EfficiencyAnalysisMeters[] {
    return meters.map(
      (ea: EfficiencyAnalysisMeters) =>
        ({
          efficiencyAnalysisId: efficiencyAnalysisId,
          meterNumber: ea.meter?.meterNumber,
          energyTypeName: ea.energyTypeName,
          value: !isNil(ea.value) && typeof ea.value === 'string' ? this.unlocalize(ea.value) : ea.value,
          isSelected: ea.isSelected,
          guid: ea.guid,
        } as EfficiencyAnalysisMeters)
    );
  }

  unlocalize(value: any) {
    return parseInt(value.toString().replace(',0', '').split(',').join('').split('.').join(''), 10);
  }
}
